import type { MessageDescriptor } from 'react-intl';

import {
  Sport,
  StatCategory,
} from '@sorare/core/src/__generated__/globalTypes';
import { statLabels } from '@sorare/core/src/lib/scoring';

import type {
  BattingStatKey,
  PitchingStatKey,
} from '#src/shared/stats/lib.tsx';
import {
  longBattingStatMessages,
  longPitchingStatMessages,
} from '#src/shared/stats/lib.tsx';

export const SORTED_STAT_CATEGORIES = [
  StatCategory.POSITIVE_DECISIVE_STAT,
  StatCategory.NEGATIVE_DECISIVE_STAT,
  StatCategory.GENERAL,
  StatCategory.DEFENDING,
  StatCategory.POSSESSION,
  StatCategory.PASSING,
  StatCategory.ATTACKING,
  StatCategory.GOALKEEPING,
  StatCategory.HITTING,
  StatCategory.PITCHING,
] as const;

export const isCategoryDecisive = (category: StatCategory) =>
  [
    StatCategory.POSITIVE_DECISIVE_STAT,
    StatCategory.NEGATIVE_DECISIVE_STAT,
  ].includesValue(category);

export const so5StatToPitchingStatKey: Record<string, PitchingStatKey> = {
  pitching_innings_pitched: 'outs',
  pitching_strikeouts: 'strikeouts',
  pitching_hits_allowed: 'hitsAllowed',
  pitching_earned_runs: 'earnedRuns',
  pitching_walks: 'walks',
  pitching_hit_batsmen: 'hitBatsmen',
  pitching_wins: 'win',
  pitching_saves: 'save',
  pitching_holds: 'hold',
  pitching_relief_appearance: 'reliefAppearance',
};

export const so5StatToBattingStatKey: Record<string, BattingStatKey> = {
  hitting_runs: 'runs',
  hitting_runs_batted_in: 'rbi',
  hitting_singles: 'singles',
  hitting_doubles: 'doubles',
  hitting_triples: 'triples',
  hitting_home_runs: 'homeRuns',
  hitting_walks: 'walks',
  hitting_strikeouts: 'strikeouts',
  hitting_stolen_bases: 'stolenBases',
  hitting_hit_by_pitch: 'hitByPitch',
  hitting_caught_stealing: 'stealsCaught',
};

export const statToLabel = (stat: string): string | MessageDescriptor => {
  if (stat in statLabels) {
    return statLabels[stat as keyof typeof statLabels];
  }

  if (stat in so5StatToBattingStatKey) {
    return longBattingStatMessages[so5StatToBattingStatKey[stat]];
  }

  if (stat in so5StatToPitchingStatKey) {
    return longPitchingStatMessages[so5StatToPitchingStatKey[stat]];
  }

  return stat;
};

export const statToValue = (
  stat: string,
  value: number | string | undefined
): string | number | undefined => {
  if (
    stat in so5StatToPitchingStatKey &&
    stat === 'pitching_innings_pitched' &&
    value
  ) {
    const innings = Math.floor(Number(value) / 3);
    const remaining = Number(value) % 3;
    return `${innings}.${remaining}`;
  }
  return value;
};

export const GAME_SCORE_TO_SPORT = {
  PlayerGameScore: Sport.FOOTBALL,
  So5Score: Sport.FOOTBALL,
  BaseballPlayerGameScore: Sport.BASEBALL,
  BasketballPlayerGameScore: Sport.NBA,
};
